// Generated by Copilot- Start
import React, { useCallback, useState } from "react";
import DtvText from "../../video-lab/dtv-text";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import Tooltip from "@directv-digital/fusion.molecules.tooltip";
import { colorPalette, MBL_SIZE, TBL_SIZE } from "@directv-digital/dds.ui.dtv-theme";
import { getAnalyticsAttrs, fireGa4ItemList } from "@directv-digital/dds.services.analytics-service";
import { IChnlInteractiveThemeUI } from "lib/helper/channel-interactive-tool-theme";
import SvodModalV2 from "./SvodModalV2";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";

export interface SvodTileV2 {
  id: string;
  name: string;
  icon: any;
  signaturePackageList: Array<{
    id: string;
    displayLabel: string;
    packageLegal: string;
    ctaObj: any;
    type: string;
  }>;
  genrePackageList: Array<{
    id: string;
    displayLabel: string;
    packageLegal: string;
    ctaObj: any;
    type: string;
  }>;
  deviceIncludedText?: string; // Optional text for SVOD package legal (e.g., "Included in package")
}

interface SvodBannerV2Props {
  svods: SvodTileV2[];
  bannerTitle?: string;
  bannerTextColor?: string;
  tooltipContent?: string;
  openSvodModal?: boolean;
  theme?: IChnlInteractiveThemeUI;
  customStyles?: any;
  pkgModalContent?: any;
  chnlSrc?: string;
}

const styles = {
  tooltipStyles: {
    opacity: "1",
    transform: "none",
    transition: "opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 133ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    transformOrigin: "left center",
    backgroundColor: "rgba(97, 97, 97, 0.92)",
    borderRadius: "4px",
    color: colorPalette.white.main,
    padding: "4px 8px",
    fontSize: "14px",
    maxWidth: "230px",
    width: "max-content",
    display: "inline-block",
    whiteSpace: "normal",
    overflowWrap: "normal",
    wordBreak: "normal",
    margin: "0",
    fontWeight: 400,
    fontFamily: "roboto-regular",
    position: "relative"
  }
};

const SvodBannerV2: React.FC<SvodBannerV2Props> = ({ svods, bannerTitle, bannerTextColor, tooltipContent, openSvodModal = true, theme, customStyles, pkgModalContent, chnlSrc }) => {
  try {
    const [activeSvod, setActiveSvod] = useState<SvodTileV2 | null>(null);

    const handleSvodClick = useCallback(
      (svod: SvodTileV2) => {
        if (!openSvodModal) {
          return;
        }

        // GA4 item list tracking
        const allPackages = [...svod.signaturePackageList, ...svod.genrePackageList];
        // eslint-disable-next-line camelcase
        const arr_shopModalEventData = allPackages.map((pkg, index) => ({
          item_id: pkg.id?.toLowerCase() || "",
          item_name: pkg.id?.toLowerCase() || "",
          item_variant: "",
          affilation: "",
          coupon: "",
          discount: "",
          index,
          item_list_id: "",
          item_list_name: "",
          price: "",
          quantity: ""
        }));

        fireGa4ItemList("", null);
        fireGa4ItemList("view_item_list", arr_shopModalEventData);

        setActiveSvod(svod);
      },
      [openSvodModal]
    );

    if (!svods?.length) {
      return null;
    }

    return (
      <>
        <DtvBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px"
          }}
        >
          <fieldset style={{ border: "none" }}>
            <legend>
              <DtvText
                content={bannerTitle || "Subscriptions"}
                variant="body2medium"
                sx={{
                  fontSize: "16px",
                  padding: "4px 20px",
                  backgroundColor: theme?.category?.mode === "light" ? theme?.channelHeaderBackground : "transparent",
                  lineHeight: {
 xs: "20.8px",
md: "17.6px"
},
                  letterSpacing: { md: "0.48px" },
                  fontWeight: {
 xs: 500,
md: 700
},
                  "& span": { marginLeft: "6px" },
                  color: bannerTextColor || colorPalette?.neutral?.[900],
                  ...customStyles?.title
                }}
                tooltip={{
                  content: tooltipContent || "Device required for Disney Bundle offer.",
                  mode: theme?.category?.mode === "light" ? "light" : "dark",
                  placement: "right"
                }}
              />
            </legend>
            <DtvBox sx={{
 display: "flex",
gap: "12px",
flexWrap: "wrap",
rowGap: "16px"
}}>
              {svods.map((svod, index) => {
                const { icon } = svod;
                return (
                  <Tooltip
                    content={svod.name || "Click to learn more about this SVOD"}
                    key={`${svod.id || ''}-${index}`}
                    placement="bottom"
                    tooltipStyles={styles.tooltipStyles}
                    PopperProps={{
                      style: { zIndex: 3 },
                      modifiers: [
                        {
                          name: "preventOverflow",
                          enabled: true,
                          phase: "main"
                        },
                        {
                          name: "flip",
                          enabled: true,
                          phase: "main"
                        },
                        {
                          name: "offset",
                          options: {
                            offset: [0, -20]
                          }
                        }
                      ],
                      sx: {
                        "& .MuiTypography-root": {
                          fontFamily: `roboto-regular`,
                          fontSize: "14px"
                        }
                      }
                    }}
                  >
                    <DtvBox
                      onClick={openSvodModal ? () => handleSvodClick(svod) : undefined}
                      {...(openSvodModal ?
                        getAnalyticsAttrs(svod.name?.toLowerCase() || "", "panel_channel lineup", `/modal_svod_${svod.name?.toLowerCase() || ""}`) :
                        {})}
                      sx={{ cursor: openSvodModal ? "pointer" : "auto" }}
                    >
                      <picture style={{
 width: "100%",
height: "100%"
}}>
                        <source media={MBL_SIZE} srcSet={icon?.mobile || icon?.default} />
                        <source media={TBL_SIZE} srcSet={icon?.tablet || icon?.default} />
                        <img
                          alt={svod.name}
                          src={icon?.default || icon}
                          style={{
                            display: "inline-flex",
                            verticalAlign: "middle",
                            height: customStyles?.imgHeight || "66px",
                            width: customStyles?.imgWidth || "116px",
                            paddingTop: "1.3px",
                            fontSize: "8px",
                            fill: colorPalette?.neutral?.[600],
                            position: "relative",
                            objectFit: "contain"
                          }}
                        />
                      </picture>
                    </DtvBox>
                  </Tooltip>
                );
              })}
            </DtvBox>
          </fieldset>
        </DtvBox>

        <SvodModalV2 chnlSrc={chnlSrc} open={Boolean(activeSvod)} pkgModalContent={pkgModalContent} svod={activeSvod} theme={theme} onClose={() => setActiveSvod(null)} />
      </>
    );
  } catch (error) {
    console.error("Error in SvodBannerV2 >> ", error);
    return <ErrorDisplay component="SVOD Banner V2" error={error} />;
  }
};

export default React.memo(SvodBannerV2);
// Generated by Copilot- End
